/**
 * Slot Availability Service
 * Centralizes date/time utilities and slot availability logic
 * to eliminate code duplication in ExperienceDetail.vue
 */

/**
 * Parse a date string in various formats to a Date object
 * @param {string} dateString - Date in DD/MM/YYYY, DD/MM/YY, YYYY-MM-DD, or ISO format
 * @returns {Date|null} - Parsed Date object or null if invalid
 */
export function parseDateString(dateString) {
  if (!dateString) return null;

  try {
    if (dateString.includes('/')) {
      // Handle DD/MM/YYYY or DD/MM/YY format
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0]);
        const month = parseInt(parts[1]) - 1; // Month is 0-indexed
        const year = parts[2].length === 2 ? 2000 + parseInt(parts[2]) : parseInt(parts[2]);
        return new Date(year, month, day);
      }
    } else if (dateString.includes('-')) {
      // Handle YYYY-MM-DD or ISO format (YYYY-MM-DDTHH:mm:ss)
      const datePart = dateString.split('T')[0];
      const [year, month, day] = datePart.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
  } catch (e) {
    console.error('Error parsing date string:', dateString, e);
  }

  return null;
}

/**
 * Check if two dates are the same day (ignoring time)
 * @param {Date} date1
 * @param {Date} date2
 * @returns {boolean}
 */
export function isSameDay(date1, date2) {
  if (!date1 || !date2) return false;
  return date1.getFullYear() === date2.getFullYear() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getDate() === date2.getDate();
}

/**
 * Check if a slot's time has already passed
 * @param {string} slotDate - The slot's date string
 * @param {string} startTime - The slot's start time (HH:MM or HH:MM:SS)
 * @param {Date} selectedDateObj - The selected date from the form (optional)
 * @param {boolean} isSelectedDatePast - Whether the selected date is in the past
 * @returns {boolean} - True if the slot time has passed
 */
export function isSlotTimePast(slotDate, startTime, selectedDateObj = null, isSelectedDatePast = false) {
  if (!startTime) return false;

  // If selected date is in the past, all slots are past
  if (isSelectedDatePast) {
    return true;
  }

  // If no date selected or no slot date, can't determine
  if (!selectedDateObj && !slotDate) {
    return false;
  }

  try {
    const now = new Date();

    // Use the selected date from form if available, otherwise parse slotDate
    let dateToUse = selectedDateObj;

    if (!dateToUse && slotDate) {
      dateToUse = parseDateString(slotDate);
    }

    if (!dateToUse) {
      return false;
    }

    // Extract date components
    const year = dateToUse.getFullYear();
    const month = String(dateToUse.getMonth() + 1).padStart(2, '0');
    const day = String(dateToUse.getDate()).padStart(2, '0');

    // Ensure time is in HH:MM:SS format
    let formattedTime = startTime;
    if (formattedTime && formattedTime.length === 5) {
      formattedTime = `${formattedTime}:00`;
    }

    // Create datetime string in local timezone format (YYYY-MM-DDTHH:MM:SS)
    const slotDateTimeString = `${year}-${month}-${day}T${formattedTime}`;
    const slotDateTime = new Date(slotDateTimeString);

    // Check if valid date and if it's in the past
    if (isNaN(slotDateTime.getTime())) {
      return false;
    }

    // Compare with current time
    return slotDateTime < now;
  } catch (e) {
    console.error('Error checking slot time:', e);
    return false;
  }
}

/**
 * Convert date from DD/MM/YYYY format to YYYY-MM-DD for API
 * @param {string} dateString - Date in DD/MM/YYYY format
 * @returns {string|null} - Date in YYYY-MM-DD format or null
 */
export function formatDateForApi(dateString) {
  if (!dateString) return null;

  const parts = dateString.split('/');
  if (parts.length === 3) {
    // DD/MM/YYYY format
    return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
  }

  // Try to parse as date and format
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
  } catch (e) {
    console.error('Error formatting date for API:', dateString, e);
  }

  return null;
}

/**
 * Determine the unavailability reason for a slot
 * @param {boolean} isArchived - Whether the slot is archived
 * @param {boolean} isPastTime - Whether the slot time has passed
 * @param {boolean} hasCapacity - Whether the slot has capacity for requested people (minPeople check + remaining spots)
 * @param {boolean} isFull - Whether the slot has no remaining spots for the requested group size
 * @returns {string|null} - The unavailability reason or null if available
 */
export function getUnavailabilityReason(isArchived, isPastTime, hasCapacity, isFull = false) {
  if (isArchived) return 'archived';
  if (isPastTime) return 'past';
  if (isFull) return 'full';
  if (!hasCapacity) return 'capacity';
  return null;
}

/**
 * Sort slots by date and time for display
 * @param {Array} slots - Array of slot objects with date and startTime
 * @returns {Array} - Sorted array of slots
 */
export function sortSlotsByDateTime(slots) {
  return [...slots].sort((a, b) => {
    // First compare by date
    const dateA = parseDateString(a.date);
    const dateB = parseDateString(b.date);

    if (dateA && dateB) {
      const dateDiff = dateA.getTime() - dateB.getTime();
      if (dateDiff !== 0) return dateDiff;
    }

    // If same date, compare by time
    const timeA = a.startTime || '00:00';
    const timeB = b.startTime || '00:00';
    return timeA.localeCompare(timeB);
  });
}

export default {
  parseDateString,
  isSameDay,
  isSlotTimePast,
  formatDateForApi,
  getUnavailabilityReason,
  sortSlotsByDateTime
};
